import React, { ReactElement, useContext, useEffect } from 'react';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import DropdownContextProvider, { DropdownContext } from 'contexts/dropdown';
import NibIcon from 'components/icon/nib-icon';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';

import type { IDropdownContext } from 'contexts/dropdown';

//#region Types
export const LIGHT_THEME = 'light';
export const DARK_THEME = 'dark';
export const ROUNDED_THEME = 'rounded';

export type FilterDropdownTheme = typeof LIGHT_THEME | typeof DARK_THEME | typeof ROUNDED_THEME;

export interface FilterDropdownProps {
  label: string | ReactElement;
  children: ReactElement;
  theme?: FilterDropdownTheme;
  className?: string;
  contentClassName?: string;
  testId?: string;
  icon?: ReactElement;
  isDefaultValue?: boolean;
}
//#endregion

export default function FilterDropdown({ label, children, theme = LIGHT_THEME, className, contentClassName, testId, icon, isDefaultValue }: FilterDropdownProps) {
  return (
    <DropdownContextProvider>
      <Dropdown label={label} theme={theme} className={className} contentClassName={contentClassName} testId={testId} icon={icon} isDefaultValue={isDefaultValue}>
        {children}
      </Dropdown>
    </DropdownContextProvider>
  );
}

function Dropdown({ label, children, theme, className, contentClassName, testId, icon, isDefaultValue }: FilterDropdownProps) {
  const { isActive, isSelected, closeDropdown, toggleDropdown, setIsSelected } = useContext(DropdownContext) as IDropdownContext;

  useEffect(() => {
    if (isDefaultValue !== undefined) {
      setIsSelected(!isDefaultValue);
    }
  }, [label, isDefaultValue, setIsSelected]);

  return (
    <div
      className={buildClassName(styles.component, isActive && styles.active, isSelected && styles.selected, styles[`theme-${theme}`], className)}
      ref={useOutsideClickHandler(closeDropdown)}
      data-testid={testId}
    >
      <div className={styles.dropdown} onClick={toggleDropdown} data-testid={`${testId}Label`}>
        {icon && icon} {label} <NibIcon className={styles.chevron} />
      </div>
      <div className={buildClassName(styles.panel, styles[`theme-${theme}`], contentClassName)}>
        {children}
      </div>
    </div>
  );
}
